// EPSE - Energy Profile & Simulation Engine Types

export interface TimeContext {
  hour_of_day: number;
  quarter_of_day: number;
  is_night: boolean;
  day_type: 'werkdag' | 'weekend' | 'feestdag';
  season: 'winter' | 'lente' | 'zomer' | 'herfst';
  within_business_hours: boolean;
  shift_id: string | null;
  month: number;
  week_of_year: number;
}

export interface PeakPeriod {
  id: string;
  start_hour: number;    // bijv. 6.5 voor 06:30
  end_hour: number;      // bijv. 7.5 voor 07:30
  intensity: number;     // 0.5-1.5, relatieve pieksterkte
  label?: string;        // "Ochtendpiek"
}

export interface SyntheticProfileConfig {
  annual_kwh: number;
  connection_kw_limit: number;
  day_night_ratio: number; // 0-1, percentage dag (0.7 = 70% dag)
  season_distribution: {
    winter: number;  // Percentage (0.35 = 35% van jaarverbruik)
    lente: number;   // 0.20
    zomer: number;   // 0.15
    herfst: number;  // 0.30
  };
  peak_periods: PeakPeriod[];  // Meerdere piekmomenten per dag
  base_load_ratio: number;  // Basisbelasting als % van piek (0.2)
  weekend_reduction: number; // Reductie op weekend (0.3 = 30% minder)
  // Nieuwe velden voor handmatig piekvermogen en PV
  peak_kw_manual: number;       // Handmatig ingesteld piekvermogen
  use_manual_peak: boolean;     // Toggle auto vs handmatig
  pv_capacity_kwp: number;      // PV installatie capaciteit
  pv_profile_type: 'none' | 'east_west' | 'south'; // PV oriëntatie
}

export interface EPSESite {
  id: string;
  tenant_id: string | null;
  org_id: string | null;
  sector_type_id: string | null;
  name: string;
  annual_kwh_total: number | null;
  annual_kwh_source: string | null;
  connection_kw_limit: number;
  operating_schedule: OperatingSchedule;
  tags: string[];
  pv_installed_kwp: number | null;
  pv_annual_kwh: number | null;
  metadata: Record<string, unknown>;
  profile_mode?: 'measured' | 'synthetic';
  synthetic_config?: SyntheticProfileConfig | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  organization?: {
    id: string;
    name: string;
  };
  sector_type?: EPSESectorType;
}

export interface OperatingSchedule {
  shifts?: Shift[];
  business_hours?: {
    start: string; // "08:00"
    end: string;   // "17:00"
  };
  working_days?: number[]; // 0-6, where 0 = Sunday
}

export interface Shift {
  id: string;
  name: string;
  start_time: string; // "06:00"
  end_time: string;   // "14:00"
  days: number[];     // 0-6
}

export interface EPSEQuarterData {
  id: string;
  site_id: string;
  start_time: string;
  end_time: string;
  kw_value: number;
  value_type: 'avg' | 'max';
  pv_kw: number | null;
  time_context: TimeContext;
  created_at: string;
}

export interface EPSEProfile {
  id: string;
  site_id: string;
  profile_type: 'measured' | 'reference' | 'synthetic';
  day_type: string | null;
  season: string | null;
  normalized_shape: number[]; // 96 values (one per quarter)
  statistics: ProfileStatistics;
  data_quality_score: number | null;
  created_at: string;
  updated_at: string;
}

export interface ProfileStatistics {
  p50: number;
  p95: number;
  p99: number;
  max: number;
  mean: number;
  std_dev: number;
  ramp_rate_avg: number;
  ramp_rate_max: number;
  night_base_p10: number;
  total_kwh: number;
  peak_hours: number[];
}

export interface BESSConfig {
  capacity_kwh: number;
  power_kw: number;
  efficiency_charge: number;
  efficiency_discharge: number;
  soc_min: number;
  soc_max: number;
  soc_initial: number;
  c_rate_max: number;
}

export interface EPSESimulation {
  id: string;
  site_id: string;
  name: string | null;
  bess_config: BESSConfig;
  dispatch_mode: 'rule_based' | 'perfect_foresight';
  results: SimulationResults;
  kpis: SimulationKPIs;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  created_by: string | null;
}

export interface SimulationResults {
  soc_trajectory: SOCPoint[];
  grid_import: number[];
  grid_export: number[];
  battery_charge: number[];
  battery_discharge: number[];
  peak_exceedances: PeakExceedance[];
  daily_summaries: DailySummary[];
}

export interface SOCPoint {
  timestamp: string;
  soc_kwh: number;
  soc_percent: number;
}

export interface PeakExceedance {
  timestamp: string;
  kw_value: number;
  exceedance_kw: number;
  duration_minutes: number;
}

export interface DailySummary {
  date: string;
  import_kwh: number;
  export_kwh: number;
  self_consumed_pv_kwh: number;
  curtailed_pv_kwh: number;
  peak_kw: number;
  cycles: number;
}

export interface SimulationKPIs {
  import_kwh: number;
  export_kwh: number;
  self_consumed_pv_kwh: number;
  curtailed_pv_kwh: number;
  peak_overruns_count: number;
  peak_overruns_duration_hours: number;
  energy_above_limit_kwh: number;
  bess_cycles: number;
  bess_throughput_kwh: number;
  peak_reduction_kw: number;
  peak_reduction_percent: number;
}

export interface EPSEReferenceProfile {
  id: string;
  name: string;
  sector: string | null;
  sector_type_id: string | null;
  sector_type?: EPSESectorType;
  tags: string[];
  day_type: string | null;
  season: string | null;
  normalized_shape: number[];
  statistics: ProfileStatistics;
  source_description: string | null;
  source_site_ids: string[];
  source_site_count: number;
  aggregation_method: 'mean' | 'median' | 'p75';
  last_aggregated_at: string | null;
  is_public: boolean;
  created_at: string;
  created_by: string | null;
}

export interface EPSESectorType {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  default_config: Partial<SyntheticProfileConfig>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Computed fields from joins
  sites_count?: number;
  reference_profiles?: EPSEReferenceProfile[];
}

export interface DataQualityReport {
  total_quarters: number;
  expected_quarters: number;
  missing_quarters: number;
  missing_percent: number;
  coverage_score: number;
  annual_kwh_calculated: number;
  annual_kwh_mismatch_percent: number | null;
  warnings: DataQualityWarning[];
}

export interface DataQualityWarning {
  type: 'missing_data' | 'mismatch' | 'outlier' | 'gap';
  severity: 'info' | 'warning' | 'error';
  message: string;
  details?: Record<string, unknown>;
}

export interface EPSEReportSection {
  id: string;
  title: string;
  content: string;
  charts?: ReportChart[];
  tables?: ReportTable[];
}

export interface ReportChart {
  type: 'line' | 'bar' | 'area' | 'heatmap';
  title: string;
  data: Record<string, unknown>[];
  config: Record<string, unknown>;
}

export interface ReportTable {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}

// Live simulation result for real-time updates
export interface LiveSimulationResult {
  kpis: SimulationKPIs;
  chartData: LiveChartPoint[];
  isCalculating: boolean;
}

export interface LiveChartPoint {
  quarter: number;
  time: string;
  load: number;
  batteryAction: number;
  netLoad: number;
  soc: number;
  socPercent: number;
  shortfall_kw: number; // Tekort dat niet gedekt kan worden
}

// Dutch holidays for time context
export const NL_HOLIDAYS_2024: string[] = [
  '2024-01-01', // Nieuwjaarsdag
  '2024-03-29', // Goede Vrijdag
  '2024-03-31', // Pasen
  '2024-04-01', // Tweede Paasdag
  '2024-04-27', // Koningsdag
  '2024-05-05', // Bevrijdingsdag
  '2024-05-09', // Hemelvaartsdag
  '2024-05-19', // Pinksteren
  '2024-05-20', // Tweede Pinksterdag
  '2024-12-25', // Eerste Kerstdag
  '2024-12-26', // Tweede Kerstdag
];

export const NL_HOLIDAYS_2025: string[] = [
  '2025-01-01', // Nieuwjaarsdag
  '2025-04-18', // Goede Vrijdag
  '2025-04-20', // Pasen
  '2025-04-21', // Tweede Paasdag
  '2025-04-26', // Koningsdag (27 april valt op zondag)
  '2025-05-05', // Bevrijdingsdag
  '2025-05-29', // Hemelvaartsdag
  '2025-06-08', // Pinksteren
  '2025-06-09', // Tweede Pinksterdag
  '2025-12-25', // Eerste Kerstdag
  '2025-12-26', // Tweede Kerstdag
];
